import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Input,
  Button,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "@/Components/ui";;
import { Link } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import Select from "react-select";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import ServerPaginatedTable from "../../Components/Common/ServerPaginatedTable";
import { useMessages, useSiteValidation } from "../../hooks/useMessaging";
import { siteService } from "../../api/services/site.service";
import { SITE_ID, SUBDOMAIN } from "../../assets/meta/meta";

const MessageHistory = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState({
    id: SITE_ID, subdomain: SUBDOMAIN
  });
  const [messageTypeFilter, setMessageTypeFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [dateFilter, setDateFilter] = useState({
    date_from: '',
    date_to: ''
  });

  const {
    messages,
    pagination,
    loading,
    error,
    fetchMessages,
    checkMessageStatus,
  } = useMessages();

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.getSitesApp();
        setSites(response.data.result.sites);
      } catch (error) {
        console.error("Failed to fetch sites:", error);
        toast.error("사이트 목록을 불러오는데 실패했습니다.");
      }
    };

    fetchSites();
  }, []);

  const handleFilterChange = () => {
    const filters = {};

    if (selectedSite) filters.site_id = selectedSite.id;
    if (messageTypeFilter) filters.message_type = messageTypeFilter;
    if (phoneFilter) filters.phone_number = phoneFilter;
    if (dateFilter.date_from) filters.date_from = dateFilter.date_from;
    if (dateFilter.date_to) filters.date_to = dateFilter.date_to;

    fetchMessages(filters);
  };

  const handleRefreshStatus = async (messageId) => {
    try {
      const response = await checkMessageStatus(messageId);
      if (response) {
        toast.success("메시지 상태가 업데이트되었습니다.");
        handleFilterChange(); // Refresh the list
      }
    } catch (error) {
      toast.error("상태 조회에 실패했습니다.");
    }
  };

  const getStatusBadge = (statusCode, statusMessage) => {
    if (statusCode === '200' || statusCode === 'success') {
      return <Badge color="success">성공</Badge>;
    } else if (statusCode === '400' || statusCode === 'failed') {
      return <Badge color="danger">실패</Badge>;
    } else if (statusCode === '100' || statusCode === 'pending') {
      return <Badge color="warning">대기</Badge>;
    } else {
      return <Badge color="secondary">{statusCode}</Badge>;
    }
  };

  const getMessageTypeBadge = (messageType) => {
    const types = {
      'AT': { label: '알림톡', color: 'primary' },
      'FT': { label: '친구톡', color: 'info' },
      'SMS': { label: 'SMS', color: 'warning' },
      'LMS': { label: 'LMS', color: 'warning' }
    };
    const type = types[messageType] || { label: messageType, color: 'secondary' };
    return <Badge color={type.color}>{type.label}</Badge>;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      enableColumnFilter: false,
      size: 80,
    },
    {
      Header: "사이트",
      accessor: "site",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const site = sites.find(s => s.id === row.original.site);
        return site ? site.name : row.original.site;
      },
    },
    {
      Header: "유형",
      accessor: "message_type",
      enableColumnFilter: false,
      cell: ({ row }) => getMessageTypeBadge(row.original.message_type),
      size: 100,
    },
    {
      Header: "전화번호",
      accessor: "phone_number",
      enableColumnFilter: false,
      size: 120,
    },
    {
      Header: "메시지",
      accessor: "message_content",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div className="text-truncate" style={{ maxWidth: '200px' }}>
          {row.original.message_content}
        </div>
      ),
    },
    {
      Header: "상태",
      accessor: "status_code",
      enableColumnFilter: false,
      cell: ({ row }) => getStatusBadge(row.original.status_code, row.original.status_message),
      size: 100,
    },
    {
      Header: "발송시간",
      accessor: "sent_at",
      enableColumnFilter: false,
      cell: ({ row }) => formatDateTime(row.original.sent_at),
      size: 150,
    },
    {
      Header: "작업",
      accessor: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="a" className="btn btn-soft-secondary btn-sm">
            <i className="ri-more-fill"></i>
          </DropdownToggle>
          <DropdownMenu className="dropdown-menu-end">
            <DropdownItem onClick={() => handleRefreshStatus(row.original.id)}>
              <i className="ri-refresh-line align-bottom me-2 text-muted"></i>
              상태 새로고침
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => console.log('View details:', row.original)}>
              <i className="ri-eye-line align-bottom me-2 text-muted"></i>
              상세보기
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      size: 100,
    },
  ];

  const siteOptions = sites.map(site => ({
    value: site.id,
    label: `${site.name} (${site.subdomain})`,
    site: site,
  }));

  const messageTypeOptions = [
    { value: 'AT', label: '알림톡' },
    { value: 'FT', label: '친구톡' },
    { value: 'SMS', label: 'SMS' },
    { value: 'LMS', label: 'LMS' }
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="메시지 내역" pageTitle="메시징" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="card-title mb-0">메시지 발송 내역</h5>
                    </Col>
                    <Col className="col-auto">
                      <Link to="/messaging-send">
                        <Button color="primary" size="sm">
                          <i className="ri-add-line align-bottom me-1"></i>
                          메시지 보내기
                        </Button>
                      </Link>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <Select
                        value={siteOptions.find(option => option.value === selectedSite?.id)}
                        onChange={(option) => setSelectedSite(option?.site || null)}
                        options={siteOptions}
                        placeholder="사이트 선택"
                        // isClearable
                        isDisabled
                      />
                    </Col>
                    <Col md={2}>
                      <Select
                        value={messageTypeOptions.find(option => option.value === messageTypeFilter)}
                        onChange={(option) => setMessageTypeFilter(option?.value || '')}
                        options={messageTypeOptions}
                        placeholder="메시지 유형"
                        isClearable
                      />
                    </Col>
                    <Col md={2}>
                      <Input
                        type="text"
                        placeholder="전화번호"
                        value={phoneFilter}
                        onChange={(e) => setPhoneFilter(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <CommonDatePicker
                        value={dateFilter.date_from}
                        clearable
                        onChangeValue={(value) => setDateFilter((prev) => ({ ...prev, date_from: value }))}
                      />
                    </Col>
                    <Col md={2}>
                      <CommonDatePicker
                        value={dateFilter.date_to}
                        clearable
                        onChangeValue={(value) => setDateFilter((prev) => ({ ...prev, date_to: value }))}
                      />
                    </Col>
                    <Col md={1}>
                      <Button color="primary" onClick={handleFilterChange} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : <i className="ri-search-line"></i>}
                      </Button>
                    </Col>
                  </Row>

                  {error && (
                    <div className="alert alert-danger">
                      <strong>오류:</strong> {error}
                    </div>
                  )}

                  <ServerPaginatedTable
                    columns={columns}
                    data={messages || []}
                    count={pagination.count}
                    next={pagination.next}
                    previous={pagination.previous}
                    onRequestData={(url) => {
                      // Extract page from URL and fetch
                      const urlParams = new URL(url, window.location.origin);
                      const page = urlParams.searchParams.get('page');
                      const filters = {};

                      if (selectedSite) filters.site_id = selectedSite.id;
                      if (messageTypeFilter) filters.message_type = messageTypeFilter;
                      if (phoneFilter) filters.phone_number = phoneFilter;
                      if (dateFilter.date_from) filters.date_from = dateFilter.date_from;
                      if (dateFilter.date_to) filters.date_to = dateFilter.date_to;
                      if (page) filters.page = page;

                      fetchMessages(filters);
                    }}
                    loading={loading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Toaster position="top-center" />
    </React.Fragment>
  );
};

export default MessageHistory;
